import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageCircleIcon, MoonStarIcon, PhoneIcon } from 'lucide-react';
import { PrimaryLink, SecondaryLink } from './Buttons';
import { contact } from '../data/contact';
import { heroBackgroundSrc, heroVideoSrc } from '../data/media';
interface HeroProps {
  lockup: string;
}
export function Hero({
  lockup
}: HeroProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  /**
   * El video se reproduce con sonido cuando el usuario llega a él.
   * Si el navegador bloquea el audio automático, se reproduce en silencio y el
   * sonido se activa en el primer gesto del usuario (toque, scroll o tecla).
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let pendingGesture = false;
    const gestureEvents = ['pointerdown', 'keydown', 'touchstart'] as const;
    const removeGestureListeners = () => {
      gestureEvents.forEach(event => document.removeEventListener(event, onGesture));
      pendingGesture = false;
    };
    function onGesture() {
      const el = videoRef.current;
      if (!el || cancelled) return;
      el.muted = false;
      el.volume = 1;
      void el.play().catch(() => undefined);
      removeGestureListeners();
    }
    const addGestureListeners = () => {
      if (pendingGesture) return;
      pendingGesture = true;
      gestureEvents.forEach(event => document.addEventListener(event, onGesture, {
        passive: true
      }));
    };
    const playWithSound = () => {
      const el = videoRef.current;
      if (!el || cancelled) return;
      el.muted = false;
      el.volume = 1;
      el.play().catch(() => {
        if (cancelled) return;
        el.muted = true;
        void el.play().catch(() => undefined);
        addGestureListeners();
      });
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const el = videoRef.current;
        if (!el) return;
        if (entry.isIntersecting) {
          playWithSound();
        } else {
          el.pause();
        }
      });
    }, {
      threshold: 0.5
    });
    observer.observe(video);
    return () => {
      cancelled = true;
      observer.disconnect();
      removeGestureListeners();
    };
  }, []);
  return <section id="inicio" aria-labelledby="hero-title" className="relative isolate overflow-hidden bg-night" data-id="element-82" data-mp="deyks55fe5cn">
      <img src={heroBackgroundSrc} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-center opacity-30" data-id="element-83" data-mp="161t1cqz4gkku" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-carbon/90 via-indigo-deep/85 to-carbon" data-id="element-84" data-mp="114svag29vhtq" />
      <div aria-hidden="true" className="absolute left-1/2 top-24 h-80 w-[42rem] -translate-x-1/2 rounded-full bg-accent/20 blur-[140px]" data-id="element-85" data-mp="3lt75ys5mbtus" />

      <div className="relative mx-auto w-full max-w-5xl px-5 py-16 sm:px-8 sm:py-24 lg:py-28" data-id="element-86" data-mp="3htpzbveggqo1">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="flex flex-col items-center text-center" data-id="element-87" data-mp="1x7htx8b35sy8">
          {/* 1. Nombre / logo de la marca */}
          <div className="flex items-center gap-3" data-id="element-88" data-mp="2b20s17jzyaaq">
            <MoonStarIcon className="h-5 w-5 shrink-0 text-accent-soft" aria-hidden="true" data-id="element-89" data-mp="2g0c3fmpxwsv2" />
            <span className="font-display text-[0.7rem] font-medium uppercase leading-tight tracking-[0.26em] text-frost sm:text-sm sm:tracking-[0.3em]" data-id="element-90" data-mp="2kxc5znnkzdhf">
              {lockup}
            </span>
          </div>

          {/* 2. Título principal */}
          <h1 id="hero-title" className="mt-8 max-w-3xl font-display text-[1.85rem] font-light leading-[1.15] tracking-[0.04em] text-frost sm:text-[2.7rem] lg:text-[3.2rem]" data-id="element-91" data-mp="umpopxvmbeww">
            RITUALES Y ORIENTACIÓN ESPIRITUAL PARA EL AMOR Y LA VIDA PERSONAL
          </h1>

          {/* 3. Subtítulo */}
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-silver sm:text-lg" data-id="element-92" data-mp="3vgnk2zqt29uj">
            Conoce prácticas tradicionales de brujería, hechicería y chamanismo a
            través de consultas personalizadas.
          </p>

          {/* 4. Video original */}
          <div className="mt-10 w-full sm:mt-12" data-id="element-93" data-mp="3hpr202ui9b1q">
            <div className="relative mx-auto w-full max-w-4xl overflow-hidden rounded-xl border border-silver/20 bg-carbon shadow-card sm:rounded-2xl" data-id="element-94" data-mp="3hqk1qjv1r0ad">
              <video ref={videoRef} src={heroVideoSrc} className="block aspect-video h-full w-full bg-carbon object-cover" controls playsInline loop preload="auto" data-id="element-95" data-mp="3rglrdg31sqre">
                Tu navegador no admite la reproducción de video.
              </video>
            </div>
          </div>

          {/* 5 y 6. Botones */}
          <div className="mt-10 flex w-full flex-col gap-3.5 sm:w-auto sm:flex-row sm:items-center sm:justify-center" data-id="element-96" data-mp="19hcfm3zqhn9k">
            <PrimaryLink href={contact.whatsappHref} className="w-full sm:w-auto" aria-label={`Hablar por WhatsApp al ${contact.whatsapp}`} data-id="element-97" data-mp="1eb6yx6bfg084">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-98" data-mp="14h7xk9j6gkh8" />
              Hablar por WhatsApp
            </PrimaryLink>
            <SecondaryLink href={contact.phoneHref} className="w-full sm:w-auto" aria-label={`Llamar ahora al ${contact.phone}`} data-id="element-99" data-mp="1x8rmma2ggsr5">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-100" data-mp="1n9sbmku7wl0z" />
              Llamar ahora
            </SecondaryLink>
          </div>

          {/* 7. Aviso para mayores de edad */}
          <p className="mt-9 max-w-xl text-xs leading-relaxed text-silver/80 sm:text-[0.8rem]" data-id="element-101" data-mp="7vo37u0j6lov">
            Servicio exclusivo para mayores de edad. Consultas y prácticas
            espirituales de carácter personal y tradicional.
          </p>
        </motion.div>
      </div>
    </section>;
}